import { FC, Fragment, MouseEventHandler, useMemo } from 'react';
import { Redirect, useLocation, useParams } from 'react-router';
import { Link } from 'react-router-dom';
import { Box, Center, Flex } from '@chakra-ui/react';
import { RTBlockNode } from '@prismicio/types';

import Button from 'components/_core/Button';
import Heading from 'components/_core/Heading';
import ErrorCard from 'components/ErrorCard';
import Loader from 'components/Loader';
import PrismicRichText from 'components/PrismicRichText';
import { logEvent } from 'features/Analytics/analytics';
import * as OfferDetailsLayout from 'layouts/OfferDetailsLayout';

import IntegrationCard from '../../components/IntegrationCard';
import PartnerCard from '../../components/PartnerCard';
import * as routes from '../../routes';
import { IntegrationDetailsRouteState } from '../../types';
import { getBackButtonUrl } from '../utils';
import useGetToolboxIntegration from './hooks/useGetToolboxIntegration';
import locales from './locales';

interface RouteParams {
  integrationId: string;
}

const IntegrationDetails: FC = () => {
  const { integrationId } = useParams<RouteParams>();
  const { state: routeState } = useLocation<IntegrationDetailsRouteState>();
  const { error, integration, loading } =
    useGetToolboxIntegration(integrationId);

  const backButtonUrl = useMemo(
    () => getBackButtonUrl(routeState),
    [routeState],
  );

  if (loading) {
    return <Loader />;
  }

  if (error) {
    return (
      <Center height="100%">
        <ErrorCard />
      </Center>
    );
  }

  if (!integration) {
    return <Redirect to={routes.integrations} />;
  }

  const onCtaClick: MouseEventHandler<HTMLButtonElement> = (e) => {
    // prevent native navigation
    // to avoid race condition with the logEvent
    e.preventDefault();

    logEvent({
      name: 'ToolboxIntegrationDetails External Link Clicked',
      properties: {
        integrationTitle: integration.title,
        partnerName: integration.partner?.name,
      },
      usePrefix: false,
    });

    window.open(integration.link as string, '_blank');
  };

  return (
    <OfferDetailsLayout.PageContainer
      trackViewProps={{
        eventProperties: {
          ...routeState,
          integrationTitle: integration.title,
          partnerName: integration.partner?.name,
        },
        pageName: 'ToolboxOfferDetails Viewed',
        usePrefix: false,
      }}
    >
      <OfferDetailsLayout.BackButton to={backButtonUrl} />

      <OfferDetailsLayout.StickyHeader>
        <OfferDetailsLayout.HeaderTitle>
          {integration.title}
        </OfferDetailsLayout.HeaderTitle>

        {integration.link ? (
          <OfferDetailsLayout.HeaderCta
            as="a"
            href={integration.link}
            onClick={onCtaClick}
            target="_blank"
          >
            {locales.integration.connect}
          </OfferDetailsLayout.HeaderCta>
        ) : null}
      </OfferDetailsLayout.StickyHeader>

      <OfferDetailsLayout.ContentWrapper
        aside={
          integration.partner ? (
            <OfferDetailsLayout.Aside>
              <PartnerCard partner={integration.partner} />
            </OfferDetailsLayout.Aside>
          ) : null
        }
      >
        {integration.perk ? (
          <Box>
            <PrismicRichText richText={integration.perk as RTBlockNode[]} />
          </Box>
        ) : null}

        {integration.content?.map((content, i) => (
          <Fragment key={`${content?.__typename}${i}`}>
            {content?.title ? (
              <PrismicRichText richText={content.title as RTBlockNode[]} />
            ) : null}

            {content?.content ? (
              <PrismicRichText richText={content.content as RTBlockNode[]} />
            ) : null}
          </Fragment>
        ))}
      </OfferDetailsLayout.ContentWrapper>

      <Box paddingBottom="space-24">
        <Flex alignItems="center" justifyContent="space-between">
          <Heading>{locales.integrations.similar}</Heading>

          <Button as={Link} to={routes.integrations} variant="inline-primary">
            {locales.integrations.seeAll}
          </Button>
        </Flex>
      </Box>

      {integration.similarIntegrations ? (
        <OfferDetailsLayout.ListGrid paddingBottom="0">
          {integration.similarIntegrations.map((similarIntegration) => (
            <IntegrationCard.Link
              key={similarIntegration.toolboxIntegrationId}
              to={{
                pathname: routes.getIntegrationPath(
                  similarIntegration.toolboxIntegrationId,
                ),
                state: {
                  from: 'similar-integrations',
                  integrationId: integration.toolboxIntegrationId,
                },
              }}
            >
              <IntegrationCard partner={similarIntegration.partner}>
                {similarIntegration.description}
              </IntegrationCard>
            </IntegrationCard.Link>
          ))}
        </OfferDetailsLayout.ListGrid>
      ) : null}
    </OfferDetailsLayout.PageContainer>
  );
};

export default IntegrationDetails;
